import { useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Tooltip, capitalize } from '@crazyegginc/hatch';

import { useNotifications } from '/src/hooks';
import { LazyImg } from '/src/components/LazyImg';
import { deserializeTrigger } from '/src/features/goals/machines/wizard';
import { camelToSnake, capitalizeFirstLetter } from '/src/utils/string';
import { copyToClipboard, pickFields } from '/src/utils';
import { SupportLinks } from '/src/support';
import { RECORDINGS_FILTER_VERSION } from '/src/features/recordings/constants';
import { DEVICE_TYPES, SPECIAL_DATE_RANGES, LOGICALS } from '/src/features/_global/constants';
import {
  PIXEL_API_LIST,
  GOAL_TRIGGER_TYPES,
  ECOMMERCE_EVENTS_LIST_TYPE,
  ECOMMERCE_LIST,
  GOAL_PURPOSES,
  GOAL_VALUE_TYPES,
} from '/src/features/goals/constants';
import { safeJsonParse } from '../_global/utils';

function humanizePixelApi(api) {
  switch (api) {
    case PIXEL_API_LIST.TIKTOK:
      return 'TikTok';
    default:
      return capitalize(api);
  }
}

function humanizeEcommerceApi(api) {
  switch (api) {
    case ECOMMERCE_LIST.WIXAPI:
      return 'Wix';
    case ECOMMERCE_LIST.WOOCOMMERCE:
      return 'WooCommerce';
    case ECOMMERCE_LIST.GENERIC:
      return 'Other';
    default:
      return capitalize(api);
  }
}

export function triggersFromDisplayData(displayData, device = DEVICE_TYPES.DESKTOP) {
  if (!displayData || displayData === '' || displayData === 'null') return null;

  const parsed = JSON.parse(displayData);

  if (typeof parsed.triggers === 'undefined') {
    return null;
  }

  const triggers = parsed.triggers || parsed.values;

  const humanizedTriggers = triggers.map((trigger, triggerIndex) => {
    return trigger.segments
      .map((segment, index) => {
        if (typeof segment === 'string') {
          const isLink = segment.match(/https?:\/\//);
          return (
            <span
              key={`${parsed.id}_${triggerIndex}_${index}`}
              className={classNames({
                'shrink-0': !isLink,
                'min-w-0 truncate': isLink,
              })}
            >
              {segment}
            </span>
          );
        }
        if (segment && segment?.href) {
          return (
            <div className="relative min-w-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
              <GoalLinkURL url={segment.href} message={segment.message} />
            </div>
          );
        }
        if (segment && segment?.onHover) {
          if (segment.onHover.type === 'editor') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={false}
                  placement="bottom"
                  tooltipContent={
                    <LazyImg
                      className="h-[88px] w-[138px] !rounded-none"
                      alt="goal thumbnail"
                      src={segment.onHover.arguments?.[0]?.[`thumbnail_for_${device.toLowerCase()}`] ?? null}
                      width="138px"
                      height="88px"
                    />
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'scriptInstructions') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div className="w-40">
                      Read our{' '}
                      <a
                        href={SupportLinks.script.ce2Functions}
                        className="text-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        help article
                      </a>{' '}
                      on setting up code goals.
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'scriptInstructionsCopy') {
            return (
              <CopyCodeLink segment={segment} trigger={trigger} key={`Trigger-${trigger.index}-Segment-${index}`} />
            );
          } else if (segment.onHover.type === 'pixel_event') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div>
                      <span className="block font-semibold">
                        {humanizePixelApi(segment.onHover.arguments?.[0]?.api)} event:
                      </span>
                      <span className="font-normal">{segment.onHover.arguments?.[0]?.event || 'Any event'}</span>
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'ecommerce_event') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div>
                      <span className="block font-semibold">
                        {humanizeEcommerceApi(segment.onHover.arguments?.[0]?.api)} event:
                      </span>
                      <span className="font-normal">{segment.onHover.arguments?.[0]?.event || 'Any event'}</span>
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          }
          return (
            <span
              key={`${parsed.id}_${triggerIndex}_${index}`}
              className="text-link shrink-0 cursor-pointer text-2xs font-normal"
            >
              {segment?.message}
            </span>
          );
        }
        return (
          <span key={`${parsed.id}_${triggerIndex}_${index}`} className="shrink-0">
            {segment?.message}
          </span>
        );
      })
      .filter(Boolean);
  });

  return humanizedTriggers;
}

export function textOnlyTriggersFromDisplayData(displayData) {
  if (!displayData || displayData === '' || displayData === 'null') {
    return [
      <span className="leading-snug" key="api_trigger_segment_0">
        Calls an API endpoint
      </span>,
    ];
  }

  const parsed = safeJsonParse(displayData);

  if (typeof parsed.triggers === 'undefined') {
    return null;
  }

  const triggers = parsed.triggers || parsed.values;

  const noDisplayList = ['|', 'Copy script'];

  const humanizedTriggers = triggers.map((trigger, triggerIndex) => {
    const msg = trigger.segments
      .reduce((acc, segment) => {
        const msg = typeof segment === 'string' ? segment : segment?.message;
        if (!msg || noDisplayList.includes(msg)) {
          return acc;
        } else {
          return acc + ' ' + msg;
        }
      }, '')
      .trim();

    if (msg) {
      return (
        <span key={`${parsed.id}_${triggerIndex}`} className="leading-tight line-clamp-2">
          {msg}
        </span>
      );
    }
    return null;
  });

  return humanizedTriggers;
}

export function GoalTriggerSummary({
  goal,
  textOnly = false,
  centered,
  expandBehavior = 'click',
  showMaxTriggers = 2,
  showIntroText = true,
  className,
}) {
  const [expanded, setExpanded] = useState(false);
  if (!goal?.displayData) return null;

  const triggers = textOnly
    ? textOnlyTriggersFromDisplayData(goal.displayData)
    : triggersFromDisplayData(goal.displayData);

  if (triggers === null) {
    return null;
  }

  const truncatedTriggers = triggers.slice(0, showMaxTriggers ?? undefined);
  const leftOverTriggers = showMaxTriggers ? triggers.slice(showMaxTriggers) : [];
  const displayedTriggers = expanded ? triggers : truncatedTriggers;

  return (
    <div
      className={classNames('text-caption', className, {
        'w-full text-center': centered,
        'flex flex-col': !centered,
      })}
    >
      {showIntroText ? <span className="block">Goal reached when someone:</span> : null}

      <ul className="inline-block max-w-full list-outside list-disc pl-5 text-left ">
        {displayedTriggers.map((trigger, index) => (
          <li key={`Goal-${goal.id}_trigger_${index}`}>
            <div className="flex items-center space-x-1 overflow-hidden">
              {trigger}
              {displayedTriggers.length > index + 1 ? <span className="!ml-0 shrink-0">, or</span> : null}
            </div>
          </li>
        ))}
      </ul>

      {leftOverTriggers.length > 0 && expandBehavior === 'click' ? (
        <div
          className={classNames({
            'ml-5': displayedTriggers.length > 1,
            'self-start': !centered,
            'flex w-full items-center justify-center': centered,
          })}
        >
          <button type="button" className="font-semibold text-dodger-blue-500" onClick={() => setExpanded((x) => !x)}>
            {expanded ? 'Hide triggers' : `Show ${leftOverTriggers.length} more triggers`}
          </button>
        </div>
      ) : null}

      {leftOverTriggers.length > 0 && expandBehavior === 'info' ? (
        <div
          className={classNames('font-semibold', {
            'ml-5': displayedTriggers.length > 1,
            'self-start': !centered,
            'flex w-full items-center justify-center': centered,
          })}
        >
          +{leftOverTriggers.length} more triggers
        </div>
      ) : null}
    </div>
  );
}

export function GoalLinkURL({ url, message }) {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    setIsTruncated(ref.current?.clientWidth < ref.current?.scrollWidth);

    const getTruncated = () => {
      setIsTruncated(ref.current?.clientWidth < ref.current?.scrollWidth);
    };
    window.addEventListener('resize', getTruncated);

    return () => window.removeEventListener('resize', getTruncated);
  }, [message]);

  return (
    <Tooltip
      show={isTruncated}
      style={{ maxWidth: '230px' }}
      tooltipContent={<div className="break-words">{message}</div>}
    >
      <a
        href={url}
        className="text-link external-link relative flex flex-1 items-center text-2xs font-normal"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="relative min-w-0 truncate" ref={ref}>
          {message}
        </span>
      </a>
    </Tooltip>
  );
}

export function parseTriggers(triggers) {
  return triggers.map((trigger, index) => {
    const { __typename, ...payload } = trigger;
    return {
      ...payload,
      ...deserializeTrigger(trigger.filter),
      name: trigger?.name ?? '',
      expanded: index === 0,
      elementValue: trigger.elementValue
        ? {
            ...trigger.elementValue,
            syncSelectors: true,
          }
        : null,
    };
  });
}

const escapeHtml = (unsafe) => {
  return unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

export function generateCodeGoalScript(trigger, code, escaped = true) {
  if (
    trigger.valueType &&
    trigger.valueType.toUpperCase() === GOAL_VALUE_TYPES.CODE &&
    trigger.type.toUpperCase() === GOAL_TRIGGER_TYPES.CODE
  ) {
    const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}", {\n\t\t\tworth: '99.95',\n\t\t\tcurrency: 'USD'\n\t\t});\n\t});\n</script>`;
    return escaped ? escapeHtml(baseScript) : baseScript;
  }

  const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}");\n\t});\n</script>`;
  return escaped ? escapeHtml(baseScript) : baseScript;
}

export function generateEcommerceGoalScript(type) {
  if (type === ECOMMERCE_EVENTS_LIST_TYPE.ADD_TO_CART) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone adds something to the shopping cart
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.addToCart(
          {
            name: 'Product 1',  // Required.
            price: 99.95,       // Required. Price in default or specified currency.
                                //  This is the total price for _all_ items
                                //  not an individual per-item price.
            currency: 'USD',    // Optional. Three-digit currency code.
            quantity: 2,        // Optional. Defaults to &quot;1&quot; if not provided.
            productId: '12345'  // Optional.
          }
        );
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.START_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *starts* the checkout process
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.startCheckout();
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.COMPLETE_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *completed* the checkout process
      // and places an order
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.checkoutCompleted(
          total: 99.95,      // Required. The final amount when checking out
                            //   in default or specified currency.
          currency: 'USD',   // Optional. Three-digit currency code.
          orderId: '4444'    // Optional.
        );
      });
    &lt;/script&gt;`;
  }

  return null;
}

function CopyCodeLink({ trigger, segment, noParse = false, theme = 'light' }) {
  const notifications = useNotifications();
  const isNoParse = noParse ? segment : segment.onClick.arguments[0];
  const script = useMemo(() => generateCodeGoalScript(trigger, isNoParse, false), [isNoParse, trigger]);

  function handleOnClick() {
    if (noParse) {
      copyToClipboard(script);
      notifications.success({ content: 'Script copied to clipboard', timeout: 2000 });
    } else {
      segment.onClick
        ? async () => {
            copyToClipboard(script);
            notifications.success({ content: 'Script copied to clipboard', timeout: 2000 });
          }
        : null;
    }
  }

  return (
    <Tooltip
      interactive={false}
      darkTheme={true}
      placement="bottom"
      tooltipContent={<div className="w-40">Click to copy the script for this trigger</div>}
    >
      <button
        onClick={() => handleOnClick()}
        className={classNames('cursor-pointer', {
          'text-link text-2xs font-normal': !noParse && theme === 'light',
          'text-xs text-malibu-500 hover:underline': noParse && theme === 'dark',
          'text-dodger-blue-500 text-sm hover:underline': noParse && theme === 'light',
        })}
      >
        {noParse ? 'Copy script' : segment.message}
      </button>
    </Tooltip>
  );
}

export function generateRecordingsFilterUrlForGoal(data, selected = { special: SPECIAL_DATE_RANGES.LAST_30_DAYS }) {
  return JSON.stringify({
    version: RECORDINGS_FILTER_VERSION,
    operator: LOGICALS.AND,
    conditions: [
      {
        criteria: 'goal',
        comparison: 'did all of',
        values: [data.id],
      },
      {
        criteria: 'date_range',
        value: selected.special
          ? {
              special: selected.special,
            }
          : {
              start_date: selected.startDate,
              end_date: selected.endDate,
            },
      },
    ],
  });
}

const basicFields = ['autoName', 'devices', 'expanded', 'filter', 'name', 'syncSelectors', 'trigger', 'uiMode'];
const elementFields = [
  'phoneSelector',
  'tabletSelector',
  'desktopSelector',
  'targetUrl',
  'trackingOption',
  'elementSelectorType',
  'wildcardUrl',
];

const getWhitelistedFields = (type) => {
  switch (type) {
    case 'CODE':
      return [...basicFields, 'code'];
    case 'PIXEL_EVENT':
      return [...basicFields, 'pixelApi', 'pixelCustomEvent', 'pixelEvent', 'pixelId'];
    case 'ECOMMERCE_EVENT':
      return [...basicFields, 'ecommerceApi', 'ecommerceEvent'];
    case 'CLICK':
    case 'FORM_SUBMISSION':
      return [...basicFields, ...elementFields];
    case 'URL_VISIT':
      return [...basicFields, 'targetUrl'];
    default:
      return basicFields;
  }
};

export function contextToArgs(context) {
  const { payload } = context;

  const triggers = payload.triggers.map((t) => pickFields(t, getWhitelistedFields(t.trigger)));

  return {
    ...payload,
    triggers,
  };
}

export function getTriggerContent(contentType, contentFilter) {
  const type = contentType.toUpperCase();
  const parsedFilter = safeJsonParse(contentFilter);
  const notAvailable = 'N/A';

  let content = {
    title: 'Other trigger type',
    text: notAvailable,
    urlAddress: '',
    isCode: false,
  };

  switch (type) {
    case GOAL_TRIGGER_TYPES.CLICK:
      return (content = {
        title: 'Clicks on a button or link',
        text: 'on',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0].on_page : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.CODE:
      return (content = {
        title: 'Triggers a script',
        text: '',
        urlAddress: '',
        isCode: true,
      });
    case GOAL_TRIGGER_TYPES.FORM_SUBMISSION:
      return (content = {
        title: 'Fills out a form',
        text: 'on',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0].on_page : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.URL_VISIT:
      return (content = {
        title: 'Reaches page',
        text: '',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0] : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.PIXEL_EVENT:
      return (content = {
        title: 'Triggers a conversion pixel',
        text: `${parsedFilter?.conditions[0].values ? humanizePixelApi(parsedFilter.conditions[0].values[0].api) : notAvailable} event`,
        urlAddress: '',
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.API:
      return (content = {
        title: 'Called via API',
        text: '',
        urlAddress: '',
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT:
      return (content = {
        title: 'Completes an eCommerce event',
        text: `${parsedFilter?.conditions[0]?.values ? `${humanizeEcommerceApi(parsedFilter.conditions[0].values[0].api)} - ${capitalizeFirstLetter(camelToSnake(parsedFilter.conditions[0].values[0].event).replaceAll('_', ' '))}` : notAvailable}`,
        urlAddress: '',
        isCode: false,
      });
    default:
      return content;
  }
}

export function RenderTriggerText({ trigger, theme = 'dark', single = false, isDeleted = false }) {
  const filter = safeJsonParse(trigger.filter);
  const type = trigger.type ?? 'other';
  const codeHaveValue = filter.conditions[0]?.values ?? false;
  const { title, text, urlAddress, isCode } = getTriggerContent(type, filter);

  return (
    <>
      <span
        className={classNames('font-bold', {
          'text-xs mb-1': !single,
          'text-sm mb-0.25': single,
          'text-body-3': isDeleted,
        })}
      >
        {title}
        {isDeleted ? ` (deleted)` : ''}
      </span>

      {isCode && !isDeleted ? (
        <>
          {codeHaveValue ? (
            <CopyCodeLink trigger={trigger} segment={filter.conditions[0].values[0]} noParse={true} theme={theme} />
          ) : (
            <Tooltip
              interactive={true}
              darkTheme={true}
              placement="bottom"
              tooltipContent={
                <div className="w-40">
                  Read our{' '}
                  <a
                    href={SupportLinks.script.ce2Functions}
                    className="text-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    help article
                  </a>{' '}
                  on setting up code goals.
                </div>
              }
            >
              <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                Goal reached
              </span>
            </Tooltip>
          )}
        </>
      ) : (
        <span
          className={classNames({
            'text-xs truncate': !single,
            'text-sm': single,
          })}
        >
          {text && `${text} `}
          {urlAddress && (
            <a
              href={urlAddress}
              className={classNames('hover:underline', {
                'text-malibu-500': theme === 'dark',
                'text-dodger-blue-500': theme === 'light',
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {urlAddress}
            </a>
          )}
        </span>
      )}
    </>
  );
}

export function combinedTriggerAndDisplayData(goal, countsData) {
  const parsedTriggers = goal?.displayData ? JSON.parse(goal.displayData) : [];
  const triggers = goal?.triggers ?? [];

  return triggers.map((trigger) => {
    let parsedTriggersTriggers = parsedTriggers?.triggers ?? [];
    let foundTrigger = parsedTriggersTriggers.find((i) => i.id === trigger.id);
    let foundTriggerCounts = countsData?.find((i) => i.id === trigger.id) ?? [];

    return { ...trigger, segments: { ...foundTrigger?.segments }, counts: { ...foundTriggerCounts } };
  });
}

export function goalType(goal) {
  if (goal?.triggers?.some?.((t) => t.type && t.type.toUpperCase() === GOAL_TRIGGER_TYPES.API)) {
    return GOAL_TRIGGER_TYPES.API;
  }
  if (goal?.icon) {
    return goal.icon.toUpperCase();
  }
  if (goal?.triggers && goal.triggers.length) {
    const firstTrigger = goal.triggers.at(0).trigger;

    if (firstTrigger.trim().length === 0) {
      return GOAL_PURPOSES.OTHER;
    }

    return goal.triggers.at(0).trigger;
  }
  return GOAL_PURPOSES.OTHER;
}
